import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Event } from '../types';
import { apiService } from '../services/api';
import { useAuth } from './AuthContext';

interface EventContextType {
  events: Event[];
  selectedEvent: Event | null;
  selectedEventId: number | null;
  isLoadingEvents: boolean;
  isInitialized: boolean;
  error: string | null;
  selectEvent: (eventId: number) => void;
  refreshEvents: (forceRefresh?: boolean) => Promise<void>;
  isSurpriseEvent: boolean;
  userListStatus: {
    isLocked: boolean;
    lockedAt: Date | null;
    canPublish: boolean;
  } | null;
  lockUserList: (listId: number) => Promise<void>;
  refreshUserListStatus: () => Promise<void>;
}

const EventContext = createContext<EventContextType | undefined>(undefined);

interface EventProviderProps {
  children: ReactNode;
}

export const EventProvider: React.FC<EventProviderProps> = ({ children }) => {
  const { currentGroupSlug, isAuthenticated, getUserMainPerson } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupId, setGroupId] = useState<number | null>(null);
  const [loadedGroupSlug, setLoadedGroupSlug] = useState<string | null>(null);
  const [loadedEventsForGroup, setLoadedEventsForGroup] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [userListStatus, setUserListStatus] = useState<{
    isLocked: boolean;
    lockedAt: Date | null;
    canPublish: boolean;
  } | null>(null);

  // Refs to prevent duplicate calls
  const loadingGroupDataRef = useRef(false);
  const loadingEventsRef = useRef(false);
  const groupDataTimeoutRef = useRef<NodeJS.Timeout>();
  const eventsTimeoutRef = useRef<NodeJS.Timeout>();
  


  const selectedEvent = events.find(event => event.id === selectedEventId) || null;
  const isSurpriseEvent = selectedEvent?.isSurprise || false;

  // Load group data to get group ID (with caching to prevent duplicate calls)
  useEffect(() => {
    const loadGroupData = async () => {
      if (!isAuthenticated || !currentGroupSlug) {
        setGroupId(null);
        setEvents([]);
        setSelectedEventId(null);
        setLoadedGroupSlug(null);
        return;
      }

      // Skip if we already loaded this group or if already loading
      if (loadedGroupSlug === currentGroupSlug || loadingGroupDataRef.current) {
        return;
      }

      // Clear any existing timeout
      if (groupDataTimeoutRef.current) {
        clearTimeout(groupDataTimeoutRef.current);
      }

      // Debounce the API call
      groupDataTimeoutRef.current = setTimeout(async () => {
        if (loadingGroupDataRef.current) return;
        
        try {
          loadingGroupDataRef.current = true;
          const groupData = await apiService.getGroupDetails(currentGroupSlug);
          setGroupId(groupData.id);
          setLoadedGroupSlug(currentGroupSlug);
        } catch (error) {
          console.error('Failed to load group data:', error);
          setError('Errore nel caricamento del gruppo');
        } finally {
          loadingGroupDataRef.current = false;
        }
      }, 100); // 100ms debounce
    };

    loadGroupData();
    
    // Cleanup timeout on unmount
    return () => {
      if (groupDataTimeoutRef.current) {
        clearTimeout(groupDataTimeoutRef.current);
      }
    };
  }, [isAuthenticated, currentGroupSlug, loadedGroupSlug]);

  const refreshEvents = useCallback(async (forceRefresh = false) => {
    if (!groupId) return;

    // Skip if we already loaded events for this group (unless forced) or if already loading
    if ((!forceRefresh && loadedEventsForGroup === groupId) || loadingEventsRef.current) {
      return;
    }

    // Clear any existing timeout
    if (eventsTimeoutRef.current) {
      clearTimeout(eventsTimeoutRef.current);
    }

    // Debounce the API call
    eventsTimeoutRef.current = setTimeout(async () => {
      if (loadingEventsRef.current) return;
      
      try {
        loadingEventsRef.current = true;
        setIsLoadingEvents(true);
        setError(null);
        
        // Ensure we have the group context before making the call
        if (!currentGroupSlug) {
          console.warn('No currentGroupSlug available, skipping events load');
          return;
        }
        
        const eventsData = await apiService.getGroupEvents('open');
        setEvents(eventsData);
        setLoadedEventsForGroup(groupId);
      
      // Filter events to exclude past events
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Set to start of day for comparison
      
      const futureEvents = eventsData.filter((event: Event) => {
        const eventDate = new Date(event.eventDate);
        eventDate.setHours(0, 0, 0, 0); // Set to start of day for comparison
        return eventDate >= today;
      });

      // Auto-select event based on priority:
      // 1. Previously selected event (from localStorage) if it's still valid (future)
      // 2. First future open event
      if (futureEvents.length > 0) {
        const storedEventId = localStorage.getItem(`selectedEvent_${groupId}`);
        let eventToSelect: Event | undefined;
        
        if (storedEventId) {
          eventToSelect = futureEvents.find((e: Event) => e.id === Number.parseInt(storedEventId));
        }
        
        // If stored event is not found or is past, select the first future event
        eventToSelect ??= futureEvents[0];
        
        if (eventToSelect) {
          setSelectedEventId(eventToSelect.id);
        }
      } else {
        // No future events available, clear selection
        setSelectedEventId(null);
        // Clear localStorage if the stored event is past
        if (groupId) {
          localStorage.removeItem(`selectedEvent_${groupId}`);
        }
      }
      
      // Mark as initialized after events are loaded and event selection is complete
      setIsInitialized(true);
    } catch (error: any) {
      console.error('Failed to load events:', error);
      setError('Errore nel caricamento degli eventi');
        setIsInitialized(true); // Even on error, mark as initialized
      } finally {
        setIsLoadingEvents(false);
        loadingEventsRef.current = false;
      }
    }, 200); // 200ms debounce
  }, [groupId, loadedEventsForGroup, currentGroupSlug]);

  // Function to load user list status
  const loadUserListStatus = useCallback(async () => {
    if (!selectedEvent || !selectedEvent.isSurprise || !isAuthenticated) {
      setUserListStatus(null);
      return;
    }

    try {
      const mainPerson = await getUserMainPerson();
      if (!mainPerson) return;

      const userList = await apiService.getPersonListForEvent(selectedEvent.id, mainPerson.id);
      
      setUserListStatus({
        isLocked: userList?.isLocked || false,
        lockedAt: userList?.lockedAt ? new Date(userList.lockedAt) : null,
        canPublish: !userList?.isLocked && (userList?.items?.length > 0 || false)
      });
    } catch (error) {
      console.error('Failed to load user list status:', error);
      // Set default status if list doesn't exist yet
      setUserListStatus({
        isLocked: false,
        lockedAt: null,
        canPublish: false
      });
    }
  }, [selectedEvent, isAuthenticated, getUserMainPerson]);

  // Load events when group ID changes and we're in a group context
  useEffect(() => {
    if (groupId && currentGroupSlug && isAuthenticated) {
      // Add a small delay to ensure the URL context is fully established
      const timer = setTimeout(() => {
        refreshEvents();
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [groupId, currentGroupSlug, isAuthenticated, refreshEvents]);

  // Load user list status when selected event changes and it's a surprise event
  useEffect(() => {
    loadUserListStatus();
  }, [loadUserListStatus]);

  const selectEvent = useCallback((eventId: number) => {
    setSelectedEventId(eventId);
    if (groupId) {
      localStorage.setItem(`selectedEvent_${groupId}`, eventId.toString());
    }
    
    // If user is on browse-lists page, redirect to dashboard when changing event
    // This helps user understand they need to select a person again for the new event
    if (location.pathname.includes('/browse-lists') && currentGroupSlug) {
      navigate(`/${currentGroupSlug}/dashboard`);
    }
    
    // For other pages, stay on current page and let it update with new event data
  }, [groupId, location.pathname, currentGroupSlug, navigate]);

  const lockUserList = useCallback(async (listId: number) => {
    try {
      await apiService.lockList(listId);
      
      // Update user list status
      setUserListStatus(prev => prev ? {
        ...prev,
        isLocked: true,
        lockedAt: new Date(),
        canPublish: false
      } : null);
      
      // Refresh events to get updated list data
      await refreshEvents(true);
    } catch (error) {
      console.error('Failed to lock list:', error);
      throw error;
    }
  }, [refreshEvents]);

  const contextValue: EventContextType = useMemo(() => ({
    events,
    selectedEvent,
    selectedEventId,
    isLoadingEvents,
    isInitialized,
    error,
    selectEvent,
    refreshEvents: (forceRefresh?: boolean) => refreshEvents(forceRefresh),
    isSurpriseEvent,
    userListStatus,
    lockUserList,
    refreshUserListStatus: loadUserListStatus
  }), [events, selectedEvent, selectedEventId, isLoadingEvents, isInitialized, error, selectEvent, refreshEvents, isSurpriseEvent, userListStatus, lockUserList, loadUserListStatus]);

  return (
    <EventContext.Provider value={contextValue}>
      {children}
    </EventContext.Provider>
  );
};

export const useEvent = (): EventContextType => {
  const context = useContext(EventContext);
  if (context === undefined) {
    throw new Error('useEvent must be used within an EventProvider');
  }
  return context;
};